import { FieldDef, IssueDetail, IssueSummary } from "models/issue";
import { useMemo } from "react";
import { ResolutionInfo } from "models/resolutions";
import { titleize } from "helpers/string";
import { attributeValue } from "features/Issues/helpers";
import GreenStatusButton from "components/Status/GreenStatusButton";
import { FiCheckCircle } from "react-icons/fi";
import { useCurrentCompanyAdminQuery } from "features/Company/hooks";
import { useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";
import { Flex } from "@chakra-ui/react";
import UserFormFields from "features/Issues/Components/UserFormFields";
import { UserUpdateFormValues } from "components/Forms/UserUpdateForm";
import CompanyFormFields from "features/Issues/Components/CompanyFormFields";
import { CompanyUpdateFormValues } from "features/Company/DynamicForm/formDefinition";
import { IssueModalInputFields as MissingSlackUserIssueModalInputFields } from "features/Issues/Resolutions/definitions/MissingMessageServiceUser";

export function missingAttributeIssueType(issue: IssueDetail | IssueSummary) {
  if (issue?.context?.attribute === "slack_mention") {
    return `Missing ${titleize(issue.context.roleName)} Slack`;
  }
  return `Missing "${titleize(issue.context.attribute)}"`;
}

export function expandedMissingAttributeIssueType(issue: IssueDetail) {
  if (issue?.context?.attribute === "slack_mention" && issue?.user?.fullName) {
    return `Missing ${issue?.user?.fullName}'s Slack`;
  }
  return missingAttributeIssueType(issue);
}

export function missingAttributeValue(issue: IssueDetail) {
  return attributeValue({
    company: issue?.company,
    newHireJourney: issue?.newHireJourney,
    user: issue?.user,
    roleName: issue?.context?.roleName,
    attribute: issue?.context?.attribute === "slack_mention" ? "message_service_id" : issue?.context?.attribute,
  });
}

function UserInputFields({ issue }: { issue: IssueDetail }) {
  const value = missingAttributeValue(issue);
  const { data: newHireJourney } = useNewHireJourneyDetailQuery(issue?.newHireJourneyId, {
    enabled: !!issue?.newHireJourneyId,
    staleTime: 0,
  });
  const userId = issue?.context?.userId ?? newHireJourney?.newHireId;

  const fields: FieldDef<UserUpdateFormValues>[] = useMemo(() => (
    issue?.context?.attribute ? [{ name: issue?.context?.attribute as keyof UserUpdateFormValues, label: titleize(issue?.context?.attribute) }] : []
  ), [issue?.context?.attribute]);
  return (
    <Flex direction="column" gap="1">
      {userId && <UserFormFields userId={userId} fieldsDefs={fields} issue={issue} />}
      {value && <GreenStatusButton icon={FiCheckCircle}>{`${titleize(issue?.context?.attribute)} Added`}</GreenStatusButton>}
    </Flex>
  );
}

function CompanyInputFields({ issue }: { issue: IssueDetail }) {
  const value = missingAttributeValue(issue);
  const { data: company } = useCurrentCompanyAdminQuery({
    staleTime: 0,
  });
  const fields: FieldDef<CompanyUpdateFormValues>[] = useMemo(() => (
    issue?.context?.attribute ? [{ name: issue?.context?.attribute as keyof CompanyUpdateFormValues, label: `Company ${titleize(issue?.context?.attribute)}` }] : []
  ), [issue?.context?.attribute]);
  return (
    <Flex direction="column" gap="1">
      {company && <CompanyFormFields company={company} fieldsDefs={fields} issue={issue} />}
      {value && <GreenStatusButton icon={FiCheckCircle}>{`${titleize(issue?.context?.attribute)} Added`}</GreenStatusButton>}
    </Flex>
  );
}

export function IssueModalInputFields({ issue }: { issue: IssueDetail }) {
  if (issue?.context?.attribute === "slack_mention") {
    return (
      <MissingSlackUserIssueModalInputFields issue={{ ...issue, issueType: "missing_message_service_user" }} />
    );
  } else if (issue?.context?.roleName === "company") {
    return <CompanyInputFields issue={issue} />;
  } else if (issue?.id) {
    return <UserInputFields issue={issue} />;
  }
  return null;
}

export function missingAttributeResolution(issue: IssueDetail | IssueSummary): ResolutionInfo {
  let title = `No value for ${titleize(issue.context.roleName)} "${titleize(issue.context.attribute)}"`;

  let additionalContext = `At least 1 action is referencing the ${titleize(issue.context.roleName)} "${titleize(issue.context.attribute)}", and it has no value. Please add this value so the actions can be sent.`;
  if (issue.context.attribute === "slack_mention") {
    title = `Ensure the ${titleize(issue.context.roleName)} has a valid Slack account.`;

    additionalContext = `At least 1 action is trying to @mentions the ${titleize(issue.context.roleName)} in slack, but Camino cannot find the account. Ensure the Camino Work Email matches the Slack Email.`;
  }

  return {
    issueType: "missing_attribute",
    tagText: missingAttributeIssueType(issue),
    title,
    additionalContext,
    inputComponent: IssueModalInputFields,
  };
}
